import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, generateBookingNumber } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useCities, useLocalities } from '../../hooks/useLocalities';
import { useBookingSettings } from '../../hooks/useBookingSettings';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import CityLocalitySelect from '../../components/ui/CityLocalitySelect';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiAlertCircle, FiCheck } = FiIcons;

const DAY_LABELS = ['Today', 'Tomorrow', 'Day After'];

function DatePicker({ availableDates, selectedDate, onSelect, isHoliday }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {availableDates.map((dateStr, i) => {
        const holiday = isHoliday(dateStr);
        const isSelected = selectedDate === dateStr;
        const dateObj = new Date(dateStr + 'T00:00:00');
        const dayNum = dateObj.getDate();
        const month = dateObj.toLocaleDateString('en-IN', { month: 'short' });

        return (
          <button
            key={dateStr}
            type="button"
            onClick={() => {
              if (holiday) return;
              onSelect(dateStr);
            }}
            disabled={!!holiday}
            className={`relative flex flex-col items-center py-3 px-2 rounded-xl border-2 transition-all text-sm font-medium ${
              isSelected && !holiday
                ? 'border-brand-gold bg-brand-gold/10 text-brand-navy'
                : holiday
                ? 'border-red-200 bg-red-50 text-red-300 cursor-not-allowed opacity-80'
                : 'border-gray-200 text-gray-600 hover:border-brand-gold/50'
            }`}
          >
            <span className="text-xs font-semibold uppercase tracking-wider mb-0.5 text-inherit opacity-70">
              {DAY_LABELS[i]}
            </span>
            <span className="text-xl font-bold leading-none">{dayNum}</span>
            <span className="text-xs mt-0.5">{month}</span>
            {holiday && (
              <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap">
                Holiday
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function BookingFlow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState({});
  const [pickupDate, setPickupDate] = useState('');
  const [pickupSlot, setPickupSlot] = useState('');
  const [holidayWarning, setHolidayWarning] = useState('');
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedCityName, setSelectedCityName] = useState('Kanpur');
  const [address, setAddress] = useState({
    line1: '',
    locality: '',
    city: 'Kanpur',
    state: 'UP',
    pincode: '',
    landmark: '',
  });

  const { cities, loading: citiesLoading } = useCities();
  const { localities, loading: localitiesLoading, error: localitiesError } = useLocalities(selectedCityId);
  const {
    loading: settingsLoading,
    getAvailableSlots,
    isHoliday,
    getAvailableDates,
  } = useBookingSettings();

  const availableDates = getAvailableDates();
  const availableSlots = getAvailableSlots();

  useEffect(() => {
    if (cities.length > 0 && !selectedCityId) {
      const kanpur = cities.find((c) => c.name === 'Kanpur');
      if (kanpur) {
        setSelectedCityId(kanpur.id);
        setSelectedCityName(kanpur.name);
        setAddress((prev) => ({ ...prev, city: kanpur.name }));
      }
    }
  }, [cities, selectedCityId]);

  useEffect(() => {
    const fetchServices = async () => {
      const { data } = await supabase
        .from('services')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (data) setServices(data);
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (availableSlots.length === 1 && !pickupSlot) {
      setPickupSlot(availableSlots[0]);
    }
  }, [availableSlots]);

  const handleDateSelect = (dateStr) => {
    const holiday = isHoliday(dateStr);
    if (holiday) {
      setHolidayWarning(holiday.message);
      return;
    }
    setHolidayWarning('');
    setPickupDate(dateStr);
    setPickupSlot('');
    if (availableSlots.length === 1) setPickupSlot(availableSlots[0]);
  };

  const handleCityChange = (cityId) => {
    setSelectedCityId(cityId);
    const city = cities.find((c) => c.id === cityId);
    const cityName = city?.name || '';
    setSelectedCityName(cityName);
    setAddress((prev) => ({ ...prev, city: cityName, locality: '' }));
  };

  const handleLocalityChange = (locality) => {
    setAddress((prev) => ({ ...prev, locality }));
  };

  const handleQuantity = (serviceId, delta) => {
    setSelectedServices((prev) => {
      const current = prev[serviceId] || 0;
      const next = Math.max(0, current + delta);
      const updated = { ...prev, [serviceId]: next };
      if (next === 0) delete updated[serviceId];
      return updated;
    });
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const bookingNumber = generateBookingNumber();
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          booking_number: bookingNumber,
          customer_id: user.id,
          pickup_date: pickupDate,
          pickup_slot: pickupSlot,
          pickup_address: address,
          delivery_address: address,
          status: 'pending_assignment',
        })
        .select()
        .single();

      if (bookingError) throw bookingError;

      const servicesToInsert = Object.entries(selectedServices).map(
        ([serviceId, quantity]) => ({
          booking_id: booking.id,
          service_id: serviceId,
          quantity,
        })
      );

      if (servicesToInsert.length > 0) {
        const { error: servicesError } = await supabase
          .from('booking_services')
          .insert(servicesToInsert);
        if (servicesError) throw servicesError;
      }

      setStep(4);
    } catch (error) {
      console.error('Booking failed:', error);
      alert('Failed to create booking: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep2 = pickupDate && pickupSlot && address.line1.trim() && address.locality && selectedCityId;

  return (
    <div className="p-4 pt-8 pb-24">
      <div className="flex items-center gap-4 mb-6">
        {step > 1 && step < 4 && (
          <button onClick={() => setStep(step - 1)} className="p-2 bg-white rounded-full shadow-sm">
            <FiArrowLeft />
          </button>
        )}
        <h1 className="text-2xl font-bold text-brand-navy">
          {step === 1 ? 'Select Services' : step === 2 ? 'Schedule Pickup' : step === 3 ? 'Confirm Details' : 'Booking Confirmed'}
        </h1>
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <div className="space-y-4">
          {services.map((service) => (
            <Card key={service.id} className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 bg-blue-50 text-brand-navy rounded-full flex items-center justify-center">
                  <SafeIcon name={service.icon_name || 'Feather'} />
                </div>
                <div>
                  <h3 className="font-semibold">{service.name}</h3>
                  <p className="text-xs text-gray-400">Add quantity to book</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleQuantity(service.id, -1)}
                  className="h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center text-brand-navy hover:bg-gray-50"
                >-</button>
                <span className="w-4 text-center font-medium">{selectedServices[service.id] || 0}</span>
                <button
                  onClick={() => handleQuantity(service.id, 1)}
                  className="h-8 w-8 rounded-full bg-brand-navy text-white flex items-center justify-center"
                >+</button>
              </div>
            </Card>
          ))}
          <Button
            className="w-full mt-6"
            disabled={Object.keys(selectedServices).length === 0}
            onClick={() => setStep(2)}
          >
            Continue
          </Button>
        </div>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <div className="space-y-4">
          <Card>
            <h3 className="font-semibold mb-4">Select Date</h3>
            {settingsLoading ? (
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-gray-200 animate-pulse rounded-xl" />)}
              </div>
            ) : (
              <DatePicker availableDates={availableDates} selectedDate={pickupDate} onSelect={handleDateSelect} isHoliday={isHoliday} />
            )}
            {holidayWarning && (
              <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                <FiAlertCircle className="shrink-0" />
                <span><b>Holiday:</b> {holidayWarning}. Please select another date.</span>
              </div>
            )}

            {pickupDate && (
              <div className="mt-5">
                <h3 className="font-semibold mb-3">
                  {availableSlots.length === 1 ? 'Pickup Window' : 'Select Time Slot'}
                </h3>
                <div className={`grid gap-2 ${availableSlots.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => setPickupSlot(slot)}
                      className={`p-3 rounded-xl border text-sm text-center transition-colors font-medium ${
                        pickupSlot === slot
                          ? 'border-brand-gold bg-brand-gold/10 text-brand-navy'
                          : 'border-gray-200 text-gray-600 hover:border-brand-gold/50'
                      }`}
                    >
                      {slot}
                      {availableSlots.length === 1 && (
                        <span className="block text-xs text-gray-400 mt-0.5 font-normal">All-day window</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <Card>
            <h3 className="font-semibold mb-4">Pickup Address</h3>
            <div className="space-y-3">
              <Input
                label="House/Flat No, Building Name"
                value={address.line1}
                onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                placeholder="e.g. 12B, Sunshine Apartments"
              />
              <CityLocalitySelect
                cities={cities}
                localities={localities}
                selectedCityId={selectedCityId}
                selectedLocality={address.locality}
                onCityChange={handleCityChange}
                onLocalityChange={handleLocalityChange}
                citiesLoading={citiesLoading}
                localitiesLoading={localitiesLoading}
                localitiesError={localitiesError}
              />
              <Input
                label="Landmark (Optional)"
                value={address.landmark}
                onChange={(e) => setAddress({ ...address, landmark: e.target.value })}
                placeholder="e.g. Near Metro Station"
              />
              <Input
                label="Pincode (Optional)"
                value={address.pincode}
                onChange={(e) => setAddress({ ...address, pincode: e.target.value })}
                placeholder="208001"
                maxLength={6}
              />
            </div>
          </Card>

          <Button className="w-full mt-6" disabled={!canProceedStep2} onClick={() => setStep(3)}>
            Review Booking
          </Button>
        </div>
      )}

      {/* Step 3 */}
      {step === 3 && (
        <div className="space-y-4">
          <Card>
            <h3 className="font-semibold text-brand-navy mb-2">Selected Services</h3>
            <div className="space-y-2 mt-2">
              {Object.entries(selectedServices).map(([id, qty]) => {
                const s = services.find((x) => x.id === id);
                return (
                  <div key={id} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-xl">
                    <span className="text-brand-navy font-medium">{s?.name}</span>
                    <span className="bg-brand-navy text-white text-xs px-3 py-1 rounded-full font-bold">
                      {qty} Garment{qty > 1 ? 's' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-brand-navy mb-2">Schedule</h3>
            <p className="text-sm text-gray-600">
              {new Date(pickupDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <p className="text-sm text-brand-gold font-medium mt-0.5">{pickupSlot}</p>
          </Card>

          <Card>
            <h3 className="font-semibold text-brand-navy mb-2">Address</h3>
            <p className="text-sm text-gray-600">
              {address.line1}, {address.locality}, {address.city}
              {address.landmark ? ` (Near ${address.landmark})` : ''}
            </p>
          </Card>

          <Button className="w-full mt-6" isLoading={loading} onClick={handleConfirm}>
            Confirm Booking
          </Button>
        </div>
      )}

      {/* Step 4 */}
      {step === 4 && (
        <div className="text-center py-12">
          <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiCheck className="text-4xl text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-brand-navy mb-2">Booking Confirmed!</h2>
          <p className="text-gray-500 mb-8">Our driver will arrive at the scheduled time to pick up your garments.</p>
          <Button onClick={() => navigate('/app/bookings')} className="w-full">View My Bookings</Button>
          <Button variant="ghost" onClick={() => navigate('/app')} className="w-full mt-2">Back to Home</Button>
        </div>
      )}
    </div>
  );
}